import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import {
  Route, Switch, Redirect, Link, useLocation, useHistory,
} from 'react-router-dom';
import Helmet from 'react-helmet';
import { useTheme } from 'emotion-theming';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Layout from '../../shared/components/Layout/Layout';
import Icon from '../../shared/_TBComponents/Icon';
import Text from '../../shared/_TBComponents/Text';
import hexToRgba from '../../shared/utils/hexToRgba';
import {
  bootIntercomForUser, shutdownIntercom, setIntercomLauncherVisible,
} from '../../shared/utils/intercom';
import DailyBrief from '../daily-brief/DailyBrief';
import AthleteNavigator from './components/AthleteNavigator';
import CalendarNavigator from './components/CalendarNavigator';
import ManageAthletes from './components/ManageAthletes';
import ManageCoaches from './components/ManageCoaches';
import ManageGroups from './components/ManageGroups';
import ManageBlackoutDates from './components/ManageBlackoutDates';
import ManageCalendars from './components/ManageCalendars';
import ManageHub from './components/ManageHub';
import ProgramNavigator from './components/ProgramNavigator';
import ManagePrograms from './components/ManagePrograms';
import ManageExercises from './components/ManageExercises';
import EditAthleteModal from './components/presentational/modals/EditAthleteModal';
import ConfirmModal from './components/presentational/modals/ConfirmModal';
import PreviewCarousel from './components/presentational/PreviewCarousel';
import useSessionState from '../calendar/components/presentational/hooks/useSessionState';
import {
  fetchAthletes, fetchCalendars, fetchGroups, fetchPrograms,
} from './ducks/navigatorActions';
// Account-wide list, unlike fetchCalendars above — includes archived calendars.
import { fetchCalendars as fetchAllCalendars } from '../calendars/ducks/calendarsActions';

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-top: 49px;
  background: #fff;
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 85px;
  background: #fff;
  border-right: 1px solid #D6D6D6;
  padding: 16px 8px;
  gap: 16px;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const MobileHomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const TabButtonWrapper = styled.div``;

const TabButton = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 0;
  background: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${(props) => props.bg || 'transparent'};
`;

const ContentSection = styled.div`
  flex: 1;
  overflow: auto;
`;

const MobileTabCard = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  border: 1px solid #EEE;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 4px 8px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const MobileTabCardCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 6px;
  text-align: center;
  padding: 0 18px;
`;

const MobileTabArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TAB_CONFIG = [
  {
    key: 'daily-brief',
    name: 'Daily Brief',
    subtitle: 'Athlete activity snapshot',
    icon: 'feed',
    desktopHeight: '90px',
  },
  {
    key: 'athletes',
    name: 'Athletes',
    subtitle: 'Workout assignments, preview, and results.',
    icon: 'Users',
    desktopHeight: '110px',
  },
  {
    key: 'calendars',
    name: 'Calendars',
    subtitle: 'Scheduled workouts',
    icon: 'calendar2',
    desktopHeight: '90px',
  },
  {
    key: 'programs',
    name: 'Programs',
    subtitle: 'Self-paced workouts',
    icon: 'templates',
    desktopHeight: '90px',
  },
  {
    key: 'exercises',
    name: 'Exercises',
    subtitle: 'Exercise library',
    icon: 'lift',
    desktopHeight: '90px',
  },
  {
    key: 'manage',
    name: 'Manage',
    subtitle: 'Coach & account settings',
    icon: 'gear',
    desktopHeight: '90px',
  },
];

// Redirect alerts from legacy links
const LEGACY_LINK_ALERTS = {
  'manage-users': {
    title: 'Manage Users has moved',
    message: 'You can now manage Athletes, Coaches, and Groups from the Manage tab on Home.',
  },
  'manage-calendars': {
    title: 'Manage Calendars has moved',
    message: 'This is the new Calendars tab on Home. Modify Calendars one by one here, or click the Manage button to see them all in a table.',
  },
  exercises: {
    title: 'Exercises has moved',
    message: 'You can now use the Exercises tab on Home to manage your Exercises.',
  },
};

const Navigator = ({ flags }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const athlete = useSelector((state) => state.navigator.data.selectedUser);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const allCalendars = useSelector((state) => state.calendars.data.unparsedCalendars);
  const programs = useSelector((state) => state.navigator.data.programs);
  const groups = useSelector((state) => state.navigator.data.groups);
  const accountCode = currentUser?.accountCode;
  const userSettings = currentUser?.userSettings;
  const manageUsers = !!userSettings?.manageUsers;
  const exercises = !!userSettings?.exercises;
  const hasCalendarAccess = currentUser?.calAccess === null
    || (currentUser?.calAccess?.length ?? 0) > 0;
  const primaryAdmin = (currentUser?.primaryAdmin ?? 0) > 0;
  const hasGroupAccess = currentUser?.userGroupIds === null
    || (currentUser?.userGroupIds?.length ?? 0) > 0;
  // Permission flags default false until currentUser loads after a refresh.
  const isUserLoaded = !!currentUser?.id;
  const accountPrimaryColor = currentUser?.id
    ? (theme?.colorPrimary || currentUser?.userSkin?.primary || '#FF6600')
    : undefined;
  const accountPrimaryBg = accountPrimaryColor ? hexToRgba(accountPrimaryColor, 0.1) : 'transparent';

  const dailyBriefEnabled = Boolean(flags?.dailyBrief);
  const tabs = TAB_CONFIG
    .filter((t) => t.key !== 'daily-brief' || dailyBriefEnabled)
    .filter((t) => t.key !== 'exercises' || exercises)
    .filter((t) => t.key !== 'calendars' || hasCalendarAccess);

  const [isMobile, setIsMobile] = React.useState(
    window.matchMedia('(max-width: 767px)').matches,
  );

  const [isEditAthleteModalOpen, setIsEditAthleteModalOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('profile');
  const [addProgramMode, setAddProgramMode] = useState(false);
  const [legacyLinkAlert, setLegacyLinkAlert] = useState(null);

  // Show notice when arriving from a legacy Coach Tools links then remove the query param
  useEffect(() => {
    const legacyMoved = new URLSearchParams(location.search).get('legacyMoved');
    const alert = legacyMoved && LEGACY_LINK_ALERTS[legacyMoved];
    if (alert) {
      setLegacyLinkAlert(alert);
      history.replace(location.pathname);
    }
  }, [location.search, location.pathname, history]);

  // Persist last visited paths in session storage
  const [lastPath, setLastPath] = useSessionState(
    'dashboard',
    'lastPath',
    null,
  );

  const [lastAthletesPath, setLastAthletesPath] = useSessionState(
    'dashboard',
    'lastPath:athletes',
    '/dashboard/athletes',
  );

  const [lastCalendarsPath, setLastCalendarsPath] = useSessionState(
    'dashboard',
    'lastPath:calendars',
    '/dashboard/calendars',
  );

  const [lastExercisesPath, setLastExercisesPath] = useSessionState(
    'dashboard',
    'lastPath:exercises',
    '/dashboard/exercises',
  );

  const [lastProgramsPath, setLastProgramsPath] = useSessionState(
    'dashboard',
    'lastPath:programs',
    '/dashboard/programs',
  );

  const openEditAthleteModal = (tab = 'profile', addMode = false) => {
    setInitialTab(tab);
    setAddProgramMode(addMode);
    setIsEditAthleteModalOpen(true);
  };

  const closeEditAthleteModal = () => {
    setIsEditAthleteModalOpen(false);
    setAddProgramMode(false);
  };

  // Activity update for PHP
  useEffect(() => {
    if (currentUser?.id) {
      const phpAppUrl = process.env.PHP_APP_URL
        || `https://${
          process.env.ENVIRONMENT === 'PRODUCTION' ? '' : 'staging.'
        }app.teambuildr.com`;

      fetch(`${phpAppUrl}/assets/activity_update.php`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {
        // Silently fail
      });
    }
  }, [currentUser?.id]);

  // Full-screen manage pages hide the sidebar + carousel; the Manage hub and exercises tab don't.
  const isManagePage = location.pathname.startsWith('/dashboard/manage-')
    || location.pathname === '/dashboard/blackout-dates';

  // Intercom support for admin users — owned here so it persists across all Home tabs
  useEffect(() => {
    const isAdmin = currentUser?.admin;
    const userId = currentUser?.id;

    if (isAdmin && userId) {
      bootIntercomForUser(userId);
    }

    return shutdownIntercom;
  }, [currentUser?.admin, currentUser?.id]);

  // Hide the Intercom launcher on full-screen manage pages, show it everywhere else
  useEffect(() => {
    setIntercomLauncherVisible(!isManagePage);
  }, [isManagePage]);

  // Update last visited paths
  useEffect(() => {
    if (!location.pathname.startsWith('/dashboard')) return;
    if (location.pathname === '/dashboard') return;
    // Don't record full-screen manage pages as a tab's "last path".
    if (location.pathname.startsWith('/dashboard/manage-')) return;
    if (location.pathname === '/dashboard/blackout-dates') return;
    // Avoids a redirect loop if a denied coach hits this route directly.
    if (location.pathname.includes('/dashboard/exercises') && isUserLoaded && !exercises) return;

    const fullPath = `${location.pathname}${location.search || ''}`;
    setLastPath(fullPath);

    if (location.pathname.includes('/dashboard/calendars')) {
      setLastCalendarsPath(fullPath);
    } else if (location.pathname.includes('/dashboard/programs')) {
      setLastProgramsPath(fullPath);
    } else if (location.pathname.includes('/dashboard/exercises')) {
      setLastExercisesPath(fullPath);
    } else if (location.pathname.includes('/dashboard/athletes')) {
      setLastAthletesPath(fullPath);
    }
  }, [location.pathname, location.search]);

  // Guard against invalid paths
  useEffect(() => {
    if (lastAthletesPath && !lastAthletesPath.includes('/dashboard/athletes')) {
      setLastAthletesPath('/dashboard/athletes');
    }
    if (lastCalendarsPath && !lastCalendarsPath.includes('/dashboard/calendars')) {
      setLastCalendarsPath('/dashboard/calendars');
    }
    if (lastProgramsPath && !lastProgramsPath.includes('/dashboard/programs')) {
      setLastProgramsPath('/dashboard/programs');
    }
    if (lastExercisesPath && !lastExercisesPath.includes('/dashboard/exercises')) {
      setLastExercisesPath('/dashboard/exercises');
    }
  }, [lastAthletesPath, lastCalendarsPath, lastProgramsPath, lastExercisesPath]);

  useEffect(() => {
    if (accountCode) {
      batch(() => {
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchCalendars(accountCode));
        dispatch(fetchAllCalendars(accountCode));
        dispatch(fetchGroups(accountCode));
        dispatch(fetchPrograms(accountCode));
      });
    }
  }, [accountCode, dispatch]);

  // Track screen width for responsive design
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const getActiveTab = () => {
    if (location.pathname.includes('/dashboard/daily-brief')) return 'daily-brief';
    if (location.pathname.includes('/dashboard/calendars')) return 'calendars';
    if (location.pathname.includes('/dashboard/programs')) return 'programs';
    if (location.pathname.includes('/dashboard/exercises')) return 'exercises';
    if (location.pathname.includes('/dashboard/manage')) return 'manage';
    return 'athletes';
  };
  const activeTab = getActiveTab();

  const getTabPath = (tab) => {
    if (isMobile) {
      return `/dashboard/${tab}`;
    }
    if (tab === 'daily-brief') {
      return '/dashboard/daily-brief';
    }
    if (tab === 'manage') {
      return '/dashboard/manage';
    }
    // Clicking a tab that is already active resets it to empty
    if (['athletes', 'calendars', 'programs'].includes(tab) && activeTab === tab) {
      return `/dashboard/${tab}`;
    }
    if (tab === 'calendars') {
      return lastCalendarsPath?.includes('/dashboard/calendars')
        ? lastCalendarsPath
        : '/dashboard/calendars';
    }
    if (tab === 'programs') {
      return lastProgramsPath?.includes('/dashboard/programs')
        ? lastProgramsPath
        : '/dashboard/programs';
    }
    if (tab === 'exercises') {
      return lastExercisesPath?.includes('/dashboard/exercises')
        ? lastExercisesPath
        : '/dashboard/exercises';
    }
    return lastAthletesPath?.includes('/dashboard/athletes')
      ? lastAthletesPath
      : '/dashboard/athletes';
  };

  const renderMobileHomeTab = ({ key, name, icon }) => (
    <TabButtonWrapper key={key}>
      <MobileTabCard to={getTabPath(key)}>
        <MobileTabCardCenter>
          <Icon icon={icon} size={32} color={accountPrimaryColor} />
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700, letterSpacing: '0.3px' }}>
            {name}
          </Text>
        </MobileTabCardCenter>
        <MobileTabArrow>
          <svg
            width='9.412'
            height='37.416'
            viewBox='0 0 9.412 37.416'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
            focusable='false'
            role='presentation'
          >
            <path d='M1 1 L8.412 18.708 L1 36.416' stroke={accountPrimaryColor} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </MobileTabArrow>
      </MobileTabCard>
    </TabButtonWrapper>
  );

  const renderTabButton = ({ key, name, icon }) => {
    const isActive = activeTab === key;
    return (
      <TabButtonWrapper key={key}>
        <TabButton
          to={getTabPath(key)}
        >
          <IconBadge bg={isActive && accountPrimaryColor ? accountPrimaryBg : 'transparent'}>
            <Icon icon={icon} size={32} color={isActive && accountPrimaryColor ? accountPrimaryColor : '#1A1A1A'} />
          </IconBadge>
          <Text styledTextProps={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.3px',
            color: '#1A1A1A',
          }}
          >
            {name}
          </Text>
        </TabButton>
      </TabButtonWrapper>
    );
  };

  return (
    <Layout fullWidth>
      <Helmet>
        <title>Home | TeamBuildr</title>
      </Helmet>
      <Container>
        {!isManagePage && (
          <NavSection>
            {tabs.map(renderTabButton)}
          </NavSection>
        )}
        <ContentSection>
          <Switch>
            <Route exact path='/dashboard'>
              {isMobile ? (
                <MobileHomeContainer>
                  <Text styledTextProps={{ fontSize: '19px', fontWeight: 700 }}>
                    Welcome to TeamBuildr, Coach
                  </Text>
                  {tabs.map(renderMobileHomeTab)}
                </MobileHomeContainer>
              ) : (
                <Redirect to={lastPath || (dailyBriefEnabled ? '/dashboard/daily-brief' : '/dashboard/athletes')} />
              )}
            </Route>
            {dailyBriefEnabled && (
              <Route exact path='/dashboard/daily-brief'>
                <DailyBrief isMobile={isMobile} />
              </Route>
            )}
            <Route exact path='/dashboard/manage-athletes'>
              {!isUserLoaded || manageUsers ? (
                <ManageAthletes
                  backPath={lastPath || '/dashboard/athletes'}
                  onEditAthlete={openEditAthleteModal}
                  isMobile={isMobile}
                />
              ) : (
                <Redirect to='/dashboard' />
              )}
            </Route>
            <Route exact path='/dashboard/manage-coaches'>
              {!isUserLoaded || primaryAdmin || manageUsers ? (
                <ManageCoaches
                  backPath='/dashboard/manage'
                  canManageCoaches={primaryAdmin}
                />
              ) : (
                <Redirect to='/dashboard' />
              )}
            </Route>
            <Route exact path='/dashboard/manage-groups'>
              {!isUserLoaded || hasGroupAccess ? (
                <ManageGroups
                  backPath='/dashboard/manage'
                />
              ) : (
                <Redirect to='/dashboard' />
              )}
            </Route>
            <Route exact path='/dashboard/blackout-dates'>
              {!isUserLoaded || primaryAdmin ? (
                <ManageBlackoutDates
                  backPath='/dashboard/manage'
                />
              ) : (
                <Redirect to='/dashboard' />
              )}
            </Route>
            <Route path='/dashboard/athletes/:athleteId?'>
              {({ match }) => (
                <AthleteNavigator
                  athleteId={match?.params?.athleteId}
                  onEditAthlete={openEditAthleteModal}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route path='/dashboard/calendars/:calendarId?'>
              {({ match }) => (
                <CalendarNavigator
                  accountCode={accountCode}
                  calendarId={match?.params?.calendarId}
                  hasCalendarAccess={hasCalendarAccess}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route path='/dashboard/programs/:programId?'>
              {({ match }) => (
                <ProgramNavigator
                  accountCode={accountCode}
                  programId={match?.params?.programId}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route exact path='/dashboard/manage-programs'>
              <ManagePrograms backPath={lastPath || '/dashboard/programs'} isMobile={isMobile} />
            </Route>
            <Route exact path='/dashboard/manage-calendars'>
              {!isUserLoaded || hasCalendarAccess ? (
                <ManageCalendars backPath={lastPath || '/dashboard/calendars'} isMobile={isMobile} />
              ) : (
                <Redirect to='/dashboard' />
              )}
            </Route>
            <Route path='/dashboard/manage'>
              <ManageHub isMobile={isMobile} />
            </Route>
            <Route path='/dashboard/exercises'>
              {!isUserLoaded || exercises ? (
                <ManageExercises />
              ) : (
                // Not /dashboard — lastPath may already be /dashboard/exercises,
                // which would redirect-loop back here.
                <Redirect to='/dashboard/athletes' />
              )}
            </Route>
            <Route>
              {/* Catches unmatched /dashboard/* paths, e.g. a typo'd URL. */}
              <Redirect to='/dashboard/athletes' />
            </Route>
          </Switch>
        </ContentSection>
        <EditAthleteModal
          isOpen={isEditAthleteModalOpen}
          onClose={closeEditAthleteModal}
          athlete={athlete}
          calendars={calendars}
          allCalendars={allCalendars}
          programs={programs}
          groups={groups}
          initialTab={initialTab}
          addProgramMode={addProgramMode}
        />
        {!isManagePage && <PreviewCarousel />}
        <ConfirmModal
          isOpen={!!legacyLinkAlert}
          onClose={() => setLegacyLinkAlert(null)}
          onConfirm={() => setLegacyLinkAlert(null)}
          title={legacyLinkAlert?.title || ''}
          message={legacyLinkAlert?.message || ''}
          hideConfirm
          cancelLabel='Okay'
        />
      </Container>
    </Layout>
  );
};

Navigator.propTypes = {
  flags: PropTypes.shape({
    dailyBrief: PropTypes.bool,
  }),
};

Navigator.defaultProps = {
  flags: {},
};

export default withLDConsumer()(Navigator);
